import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    OnDestroy,
    output,
    signal,
    Signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { pairwise, Subject, takeUntil } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

import {
    ActionItemStyle,
    ButtonActionItem,
    DropdownDirection,
    FormSelectOption,
    IconSize,
    ShActionItemComponent,
    ShClickOutsideDirective,
    ShDroppanelComponent,
    ShIconComponent
} from '@stylehaven/components';
import { ColumnPickerGroup, ColumnSelectionChange, ShowSubfoldersChange } from './column-picker-group.entity';

export interface ColumnPickerItem {
    readonly key: string;
    readonly label: string;
    readonly checked: boolean;
    readonly pinned: boolean;
}

/** A pinned column, or the last remaining checked one, cannot be unticked — but can still be dragged. */
export type DisplayColumnPickerItem = ColumnPickerItem & { readonly locked: boolean };

export interface DisplayColumnPickerGroup {
    readonly id: string;
    readonly label?: string;
    readonly collapsed: boolean;
    readonly items: DisplayColumnPickerItem[];
    /**
     * The level's standalone "Toon submappen" checkbox state — not one of `items`, since it toggles a
     * display preference rather than a column. `undefined` renders no checkbox for this level at all.
     */
    readonly showSubfolders?: boolean;
}

interface ColumnPickerGroupState {
    readonly id: string;
    readonly label?: string;
    readonly items: ColumnPickerItem[];
    readonly showSubfolders?: boolean;
}

/**
 * Replaces `DhColumnSelectionComponent` (HFBD-11 issue 003) as the picker wired into
 * `TableHeaderComponent`. Combines checkbox toggling with `@angular/cdk` drag-and-drop
 * reordering — needed because the vendor `ShDroppanelSelectComponent` it used to delegate to
 * bakes in its own non-reorderable `<ul>`/`<li>` list, so this owns its list rendering directly
 * on top of the lower-level `sh-droppanel` shell (see HFBD-11 issue 006).
 *
 * HFBD-7a: one panel holds every hierarchy level a page renders, each as its own collapsible card
 * with its own drop list — so dragging never crosses levels — while Save/Reset/discard stay
 * panel-wide and let the container persist each level separately. A group with no label gets no
 * card chrome, which is how a flat overview keeps the single plain list it has always had.
 *
 * Checkbox/Actions columns are never part of a group's options — they're rendered as fixed cells
 * outside the configurable-columns array — so they structurally never appear as draggable items.
 */
@Component({
    standalone: true,
    selector: 'dh-column-picker',
    templateUrl: './dh-column-picker.component.html',
    styleUrls: ['./dh-column-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        ShActionItemComponent,
        ShClickOutsideDirective,
        ShDroppanelComponent,
        ShIconComponent,
        CdkDropList,
        CdkDrag,
        CdkDragHandle
    ]
})
export class DhColumnPickerComponent implements OnDestroy {
    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly destroy$ = new Subject<void>();
    private closingViaAction = false;

    openDropdown$ = new BehaviorSubject<boolean>(false);
    dropdownDirection = DropdownDirection.BelowLeft;
    readonly iconSize = IconSize;

    columnSelectButton: ButtonActionItem = {
        eventListener: () => this.toggleDropdown(),
        label: this.translateService.instant('generic-overview.column-selection.action'),
        icon: 'columns',
        style: ActionItemStyle.Light
    };

    readonly groups = input<ColumnPickerGroup[]>([]);

    readonly columnSelectionChanged = output<ColumnSelectionChange>();
    readonly showSubfoldersChanged = output<ShowSubfoldersChange>();
    readonly saveColumnSelection = output<void>();
    readonly resetColumnSelection = output<void>();
    readonly discardColumnSelection = output<void>();

    private readonly groupStates = linkedSignal<ColumnPickerGroupState[]>(() => this.buildGroupStates());

    private readonly collapsedGroupIds = signal<ReadonlySet<string>>(new Set<string>());

    readonly displayGroups: Signal<DisplayColumnPickerGroup[]> = computed(() =>
        this.groupStates().map(group => {
            const checkedCount: number = group.items.filter(item => item.checked).length;

            return {
                id: group.id,
                label: group.label,
                collapsed: this.collapsedGroupIds().has(group.id),
                items: group.items.map(item => ({
                    ...item,
                    locked: item.pinned || (item.checked && checkedCount <= 1)
                })),
                showSubfolders: group.showSubfolders
            };
        })
    );

    constructor() {
        this.openDropdown$.pipe(pairwise(), takeUntil(this.destroy$)).subscribe(([wasOpen, isOpen]) => {
            if (!wasOpen && isOpen) {
                // Discarding rewinds the container's staged keys but leaves its active columns — and so
                // this component's inputs — untouched, so nothing would otherwise clear an abandoned edit.
                this.groupStates.set(this.buildGroupStates());

                return;
            }

            if (wasOpen && !isOpen) {
                if (!this.closingViaAction) {
                    this.discardColumnSelection.emit();
                }
                this.closingViaAction = false;
            }
        });
    }

    private buildGroupStates(): ColumnPickerGroupState[] {
        return this.groups().map(group => ({
            id: group.id,
            label: group.label,
            items: this.buildItems(group),
            showSubfolders: group.showSubfolders
        }));
    }

    private buildItems(group: ColumnPickerGroup): ColumnPickerItem[] {
        const optionsByKey = new Map(group.options.map(option => [option.value, option]));
        const toItem = (option: FormSelectOption, checked: boolean): ColumnPickerItem => ({
            key: option.value,
            label: option.label,
            checked,
            pinned: group.pinnedKeys.includes(option.value)
        });

        const selectedItems: ColumnPickerItem[] = group.selectedKeys
            .map(key => optionsByKey.get(key))
            .filter((option): option is FormSelectOption => option !== undefined)
            .map(option => toItem(option, true));

        const remainingItems: ColumnPickerItem[] = group.options
            .filter(option => !group.selectedKeys.includes(option.value))
            .map(option => toItem(option, false));

        return [...selectedItems, ...remainingItems];
    }

    public toggleChecked(groupId: string, item: DisplayColumnPickerItem): void {
        if (item.locked) {
            return;
        }

        this.updateGroup(groupId, items =>
            items.map(entry => (entry.key === item.key ? { ...entry, checked: !entry.checked } : entry))
        );
    }

    public onDrop(groupId: string, event: CdkDragDrop<ColumnPickerItem[]>): void {
        this.updateGroup(groupId, items => {
            const reordered: ColumnPickerItem[] = [...items];

            moveItemInArray(reordered, event.previousIndex, event.currentIndex);

            return reordered;
        });
    }

    /** Toggles the level's standalone "Toon submappen" checkbox — a no-op for a level that has none. */
    public toggleShowSubfolders(groupId: string): void {
        const current: ColumnPickerGroupState[] = this.groupStates();
        const group: ColumnPickerGroupState | undefined = current.find(candidate => candidate.id === groupId);

        if (!group || group.showSubfolders === undefined) {
            return;
        }

        const showSubfolders: boolean = !group.showSubfolders;

        this.groupStates.set(
            current.map(candidate => (candidate.id === groupId ? { ...candidate, showSubfolders } : candidate))
        );
        this.showSubfoldersChanged.emit({ groupId, showSubfolders });
    }

    public toggleCollapsed(groupId: string): void {
        this.collapsedGroupIds.update(current => {
            const next = new Set(current);

            if (!next.delete(groupId)) {
                next.add(groupId);
            }

            return next;
        });
    }

    private updateGroup(groupId: string, update: (items: ColumnPickerItem[]) => ColumnPickerItem[]): void {
        const current: ColumnPickerGroupState[] = this.groupStates();
        const group: ColumnPickerGroupState | undefined = current.find(candidate => candidate.id === groupId);

        if (!group) {
            return;
        }

        const items: ColumnPickerItem[] = update(group.items);

        this.groupStates.set(
            current.map(candidate => (candidate.id === groupId ? { ...candidate, items } : candidate))
        );
        this.columnSelectionChanged.emit({
            groupId,
            keys: items.filter(item => item.checked).map(item => item.key)
        });
    }

    public onSave(): void {
        this.saveColumnSelection.emit();
        this.closingViaAction = true;
        this.closeDropdown();
    }

    public onReset(): void {
        this.resetColumnSelection.emit();
        this.closingViaAction = true;
        this.closeDropdown();
    }

    public toggleDropdown(): void {
        this.openDropdown$.next(!this.openDropdown$.value);
    }

    public closeDropdown(): void {
        this.openDropdown$.next(false);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.openDropdown$.complete();
    }
}
